import * as ResizablePrimitive from "react-resizable-panels"

import { cn } from "@/shared/utils/general/cn"

function ResizablePanelGroup({
  className,
  ...props
}: ResizablePrimitive.GroupProps) {
  return (
    <ResizablePrimitive.Group
      data-slot="resizable-panel-group"
      className={cn(
        "flex h-full w-full aria-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    />
  )
}

function ResizablePanel({ ...props }: ResizablePrimitive.PanelProps) {
  return <ResizablePrimitive.Panel data-slot="resizable-panel" {...props} />
}

/**
 * Resize handle — matches v1's subtle pill indicator.
 *
 * Invisible by default. On hover, shows a small rounded pill (6×30px)
 * at the vertical center. No visible border between sidebar and content.
 *
 * v1 reference: 15px hit area, 6×30px pill, alpha(text.primary, 0.2)
 *
 * handleSide controls which edge the pill appears on:
 * - "right" (default): pill at right edge → used for sidebar (pill appears right of sidebar)
 * - "left": pill at left edge, separator mirrored → used for chat (pill appears left of chat border)
 */
function ResizableHandle({
  withHandle,
  handleSide = "right",
  className,
  onPillClick,
  ...props
}: ResizablePrimitive.SeparatorProps & {
  withHandle?: boolean
  handleSide?: "left" | "right"
  onPillClick?: () => void
}) {
  return (
    <ResizablePrimitive.Separator
      data-slot="resizable-handle"
      className={cn(
        // 20px wide hit area — negative margins keep layout-neutral
        "relative flex w-5 cursor-col-resize items-center bg-transparent",
        // All negative margin on one side to avoid layout shift (CLS).
        // Previously -ml-0.5 caused a 2px shift on first render.
        handleSide === "right"
          ? "-mr-5" // sidebar: extend fully into content (no left overlap)
          : "-ml-5", // chat: extend fully into sidebar (no right overlap)
        "overflow-visible",
        "focus-visible:outline-hidden",
        // Horizontal variant (unused here but keep shadcn compat)
        "aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full",
        "[&[aria-orientation=horizontal]>div]:rotate-90",
        className,
      )}
      {...props}
    >
      {withHandle && (
        <div
          onClick={(e) => {
            e.stopPropagation()
            onPillClick?.()
          }}
          className={cn(
            "z-10 rounded-full transition-opacity duration-200 cursor-pointer",
            "h-10 w-2",
            "bg-foreground/5 opacity-0 group-hover:opacity-100 hover:bg-foreground/20",
            handleSide === "right" ? "ml-auto mr-0" : "mr-auto ml-0",
          )}
        />
      )}
    </ResizablePrimitive.Separator>
  )
}

export { ResizableHandle, ResizablePanel, ResizablePanelGroup }

export const usePanelRef = ResizablePrimitive.usePanelRef
